import React, { useEffect, useRef, useState } from "react";
import styles from "./Topo.module.css";

/*
 * Topo — the topographic contour background that runs behind every surface.
 *
 *   <Topo />                               static contours on paper
 *   <Topo tone="olive" crop="start" />     a different window of the same field
 *   <Topo tone="paper" variant="hero" />   cursor-reactive canvas (hero only)
 *
 * Always position:absolute, filling its POSITIONED parent, pointer-events:none,
 * aria-hidden, z-index 0. Mount it as the FIRST child of a position:relative
 * box and keep the content after it positioned (Section's container is), so
 * the lines stay underneath.
 *
 * Props
 *   tone       "paper" | "olive" | "void"   line colour: --paper-line on paper,
 *              --olive-line on olive and void          (default "paper")
 *   variant    "static" | "hero"                       (default "static")
 *              static  one inline <svg>, preserveAspectRatio "slice".
 *              hero    the same field on a <canvas>: iso-lines are re-traced
 *                      every animation frame, pushed radially away from the
 *                      cursor / last touch point, with a very slow ambient
 *                      drift. Pauses on a hidden tab and when off-screen.
 *                      Under prefers-reduced-motion (or without canvas) it
 *                      renders the static <svg> and never starts a loop.
 *   crop       "start" | "center" | "end"   OPTIONAL. Which window of the tall
 *              field a normal-height box shows (default "center"). Give
 *              neighbouring same-tone sections different crops so the pattern
 *              does not repeat down the page.
 *   flip       boolean, OPTIONAL. Mirrors the field horizontally — a second
 *              set of three looks for free.
 *   className  extra class on the wrapper
 *   ...rest    forwarded to the wrapper
 *
 * DETERMINISM: there is no Math.random anywhere. The noise lattice comes from
 * a seeded mulberry32 PRNG (SEED below) and the field is built from + - * and
 * Math.sqrt only, all of which are exactly specified by IEEE-754 — so the
 * static path data is byte-identical on every render, in every browser and in
 * every build. It is generated ONCE per page load (module-level memo), not per
 * render and not per instance.
 */

/* ------------------------------------------------------------ parameters -- */
const FIELD_W = 1600; // field units == CSS px at scale 1
const FIELD_H = 2400; // tall on purpose: tall sections keep natural-size loops
const MIN_RENDER_W = 1200; // the field never renders narrower than this, so a
//                            phone sees the same LARGE loops, not a shrunken map

const SEED = 20260920;
const FEATURE_PX = 820; // one noise cell. Large = sparse, sweeping lines
const OCTAVE2_FREQ = 2.17;
const OCTAVE2_GAIN = 0.26;
const LEVELS = [-0.32, -0.19, -0.06, 0.07, 0.2, 0.33]; // 6 iso-levels

const STATIC_CELL = 16; // marching-squares cell for the static path
const RESAMPLE_STEP = 26; // px between control points after resampling
const MIN_CONTOUR_LENGTH = 220; // drop specks

const HERO_CELL_PX = 16;
const HERO_MAX_COLS = 90;
const HERO_MAX_ROWS = 56;
const HERO_MAX_DPR = 1.5;
const HERO_PUSH = 0.42; // 0..1 — share of the distance a line is pushed at the core
const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";

/* ----------------------------------------------------- seeded noise field -- */
function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const PERM = new Uint8Array(512);
const GRAD_X = new Float64Array(256);
const GRAD_Y = new Float64Array(256);

(function seedLattice() {
  const rand = mulberry32(SEED);
  const order = new Uint8Array(256);
  for (let i = 0; i < 256; i += 1) {
    order[i] = i;
  }
  for (let i = 255; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const swap = order[i];
    order[i] = order[j];
    order[j] = swap;
  }
  for (let i = 0; i < 512; i += 1) {
    PERM[i] = order[i & 255];
  }
  // unit gradients by rejection sampling: no trig, so no engine-dependent bits
  for (let i = 0; i < 256; i += 1) {
    let gx = 0;
    let gy = 0;
    let len = 0;
    do {
      gx = rand() * 2 - 1;
      gy = rand() * 2 - 1;
      len = gx * gx + gy * gy;
    } while (len > 1 || len < 0.01);
    len = Math.sqrt(len);
    GRAD_X[i] = gx / len;
    GRAD_Y[i] = gy / len;
  }
})();

/* 2D gradient noise, quintic fade — C2-continuous, so iso-lines never kink. */
function noise2(x, y) {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * xf * (xf * (xf * 6 - 15) + 10);
  const v = yf * yf * yf * (yf * (yf * 6 - 15) + 10);
  const X = xi & 255;
  const Y = yi & 255;
  const aa = PERM[PERM[X] + Y];
  const ab = PERM[PERM[X] + Y + 1];
  const ba = PERM[PERM[X + 1] + Y];
  const bb = PERM[PERM[X + 1] + Y + 1];
  const n00 = GRAD_X[aa] * xf + GRAD_Y[aa] * yf;
  const n10 = GRAD_X[ba] * (xf - 1) + GRAD_Y[ba] * yf;
  const n01 = GRAD_X[ab] * xf + GRAD_Y[ab] * (yf - 1);
  const n11 = GRAD_X[bb] * (xf - 1) + GRAD_Y[bb] * (yf - 1);
  const nx0 = n00 + u * (n10 - n00);
  const nx1 = n01 + u * (n11 - n01);
  return nx0 + v * (nx1 - nx0);
}

/* The height field, in field px. `t` (seconds) slides the two octaves in
   different directions, so the hero morphs instead of merely scrolling.
   t = 0 is exactly the static SVG. */
function field(fx, fy, t) {
  const x = fx / FEATURE_PX;
  const y = fy / FEATURE_PX;
  return (
    noise2(x + t * 0.008, y + t * 0.0045) +
    OCTAVE2_GAIN *
      noise2(
        x * OCTAVE2_FREQ + 17.31 - t * 0.012,
        y * OCTAVE2_FREQ - 9.73 + t * 0.009
      )
  );
}

/* --------------------------------------------------------- marching squares -- */
/* Visits every iso-line segment of a (cols x rows)-cell grid of samples and
   calls emit(levelIndex, ax, ay, bx, by, keyA, keyB). keyA / keyB identify the
   grid edge each endpoint sits on — two segments that share a key are joined
   there, which is what lets the static builder chain them into polylines. */
function marchSquares(values, cols, rows, cellW, cellH, emit) {
  const stride = cols + 1;
  let tl = 0;
  let tr = 0;
  let br = 0;
  let bl = 0;
  let level = 0;
  let x0 = 0;
  let y0 = 0;
  let base = 0;
  let ex = 0;
  let ey = 0;
  let ek = 0;

  // 0 top · 1 right · 2 bottom · 3 left. Only called for an edge whose corners
  // straddle the level, so the divisor is never zero.
  const edge = (which) => {
    if (which === 0) {
      ex = x0 + (cellW * (level - tl)) / (tr - tl);
      ey = y0;
      ek = 2 * base;
    } else if (which === 1) {
      ex = x0 + cellW;
      ey = y0 + (cellH * (level - tr)) / (br - tr);
      ek = 2 * (base + 1) + 1;
    } else if (which === 2) {
      ex = x0 + (cellW * (level - bl)) / (br - bl);
      ey = y0 + cellH;
      ek = 2 * (base + stride);
    } else {
      ex = x0;
      ey = y0 + (cellH * (level - tl)) / (bl - tl);
      ek = 2 * base + 1;
    }
  };

  const seg = (levelIndex, from, to) => {
    edge(from);
    const ax = ex;
    const ay = ey;
    const ak = ek;
    edge(to);
    emit(levelIndex, ax, ay, ex, ey, ak, ek);
  };

  for (let j = 0; j < rows; j += 1) {
    y0 = j * cellH;
    for (let i = 0; i < cols; i += 1) {
      base = j * stride + i;
      tl = values[base];
      tr = values[base + 1];
      bl = values[base + stride];
      br = values[base + stride + 1];
      const lo = Math.min(tl, tr, bl, br);
      const hi = Math.max(tl, tr, bl, br);
      x0 = i * cellW;

      for (let l = 0; l < LEVELS.length; l += 1) {
        level = LEVELS[l];
        if (level < lo || level >= hi) {
          continue;
        }
        const code =
          (tl > level ? 8 : 0) |
          (tr > level ? 4 : 0) |
          (br > level ? 2 : 0) |
          (bl > level ? 1 : 0);
        // saddles (5, 10) are split by the cell-centre value
        const centreIn = (tl + tr + br + bl) / 4 > level;

        switch (code) {
          case 1:
          case 14:
            seg(l, 3, 2);
            break;
          case 2:
          case 13:
            seg(l, 2, 1);
            break;
          case 3:
          case 12:
            seg(l, 3, 1);
            break;
          case 4:
          case 11:
            seg(l, 0, 1);
            break;
          case 6:
          case 9:
            seg(l, 0, 2);
            break;
          case 7:
          case 8:
            seg(l, 0, 3);
            break;
          case 5:
            if (centreIn) {
              seg(l, 0, 3);
              seg(l, 2, 1);
            } else {
              seg(l, 0, 1);
              seg(l, 3, 2);
            }
            break;
          case 10:
            if (centreIn) {
              seg(l, 0, 1);
              seg(l, 3, 2);
            } else {
              seg(l, 0, 3);
              seg(l, 2, 1);
            }
            break;
          default:
            break;
        }
      }
    }
  }
}

/* ------------------------------------------------------ static path builder -- */
const SEG_STRIDE = 6; // ax, ay, bx, by, keyA, keyB

/* Chains one level's loose segments into polylines ([x, y, x, y, …]). */
function chainSegments(segs) {
  const count = segs.length / SEG_STRIDE;
  const byKey = new Map();
  const link = (key, index) => {
    const list = byKey.get(key);
    if (list) {
      list.push(index);
    } else {
      byKey.set(key, [index]);
    }
  };
  for (let s = 0; s < count; s += 1) {
    link(segs[s * SEG_STRIDE + 4], s);
    link(segs[s * SEG_STRIDE + 5], s);
  }

  const used = new Uint8Array(count);

  // Walks away from `fromKey`, appending points. True when it arrives back at
  // `stopKey` (a closed loop); false when it runs off the edge of the field.
  const walk = (points, fromKey, stopKey) => {
    let key = fromKey;
    for (;;) {
      const list = byKey.get(key);
      let next = -1;
      if (list) {
        for (let q = 0; q < list.length; q += 1) {
          if (!used[list[q]]) {
            next = list[q];
            break;
          }
        }
      }
      if (next < 0) {
        return false;
      }
      used[next] = 1;
      const o = next * SEG_STRIDE;
      if (segs[o + 4] === key) {
        points.push(segs[o + 2], segs[o + 3]);
        key = segs[o + 5];
      } else {
        points.push(segs[o], segs[o + 1]);
        key = segs[o + 4];
      }
      if (key === stopKey) {
        return true;
      }
    }
  };

  const lines = [];
  for (let s = 0; s < count; s += 1) {
    if (used[s]) {
      continue;
    }
    used[s] = 1;
    const o = s * SEG_STRIDE;
    const forward = [segs[o], segs[o + 1], segs[o + 2], segs[o + 3]];
    const closed = walk(forward, segs[o + 5], segs[o + 4]);
    if (closed) {
      // the walk re-added the start point as its last point
      forward.length -= 2;
      lines.push({ points: forward, closed: true });
    } else {
      const backward = [];
      walk(backward, segs[o + 4], -1);
      const points = [];
      for (let q = backward.length - 2; q >= 0; q -= 2) {
        points.push(backward[q], backward[q + 1]);
      }
      for (let q = 0; q < forward.length; q += 1) {
        points.push(forward[q]);
      }
      lines.push({ points, closed: false });
    }
  }
  return lines;
}

function fmt(n) {
  return String(Math.round(n * 10) / 10);
}

/* Resamples a polyline at an even arc-length step, then emits it as a uniform
   quadratic B-spline (control = sample, join = midpoint): C1-smooth, and never
   wobblier than the polyline it came from. */
function smoothPath(points, closed) {
  const n = points.length / 2;
  if (n < 2) {
    return "";
  }
  const count = closed ? n + 1 : n; // a closed loop walks back to its start
  const px = (i) => points[2 * (i % n)];
  const py = (i) => points[2 * (i % n) + 1];

  const cum = new Float64Array(count);
  for (let i = 1; i < count; i += 1) {
    const dx = px(i) - px(i - 1);
    const dy = py(i) - py(i - 1);
    cum[i] = cum[i - 1] + Math.sqrt(dx * dx + dy * dy);
  }
  const total = cum[count - 1];
  if (total < MIN_CONTOUR_LENGTH) {
    return "";
  }

  const steps = Math.max(closed ? 8 : 2, Math.round(total / RESAMPLE_STEP));
  const sampleCount = closed ? steps : steps + 1;
  const sx = new Float64Array(sampleCount);
  const sy = new Float64Array(sampleCount);
  let at = 1;
  for (let k = 0; k < sampleCount; k += 1) {
    const target = (total * k) / steps;
    while (at < count - 1 && cum[at] < target) {
      at += 1;
    }
    const span = cum[at] - cum[at - 1];
    const f = span > 0 ? (target - cum[at - 1]) / span : 0;
    sx[k] = px(at - 1) + (px(at) - px(at - 1)) * f;
    sy[k] = py(at - 1) + (py(at) - py(at - 1)) * f;
  }

  const c = sampleCount;
  const mid = (i, axis) => {
    const a = axis[i % c];
    const b = axis[(i + 1) % c];
    return fmt((a + b) / 2);
  };

  let d = "";
  if (closed) {
    d += `M${mid(c - 1, sx)} ${mid(c - 1, sy)}`;
    for (let i = 0; i < c; i += 1) {
      d += `Q${fmt(sx[i])} ${fmt(sy[i])} ${mid(i, sx)} ${mid(i, sy)}`;
    }
    return `${d}Z`;
  }

  d += `M${fmt(sx[0])} ${fmt(sy[0])}`;
  if (c === 2) {
    return `${d}L${fmt(sx[1])} ${fmt(sy[1])}`;
  }
  d += `L${mid(0, sx)} ${mid(0, sy)}`;
  for (let i = 1; i < c - 1; i += 1) {
    d += `Q${fmt(sx[i])} ${fmt(sy[i])} ${mid(i, sx)} ${mid(i, sy)}`;
  }
  return `${d}L${fmt(sx[c - 1])} ${fmt(sy[c - 1])}`;
}

function buildStaticPath() {
  const cols = Math.round(FIELD_W / STATIC_CELL);
  const rows = Math.round(FIELD_H / STATIC_CELL);
  const stride = cols + 1;
  const values = new Float64Array(stride * (rows + 1));
  for (let j = 0; j <= rows; j += 1) {
    for (let i = 0; i <= cols; i += 1) {
      values[j * stride + i] = field(i * STATIC_CELL, j * STATIC_CELL, 0);
    }
  }

  const perLevel = LEVELS.map(() => []);
  marchSquares(values, cols, rows, STATIC_CELL, STATIC_CELL, (l, ax, ay, bx, by, ka, kb) => {
    perLevel[l].push(ax, ay, bx, by, ka, kb);
  });

  let d = "";
  perLevel.forEach((segs) => {
    chainSegments(segs).forEach((line) => {
      d += smoothPath(line.points, line.closed);
    });
  });
  return d;
}

let staticPathMemo = null;

/* Exported for tests / tooling: the memoised path data of the static field. */
export function getTopoPath() {
  if (staticPathMemo === null) {
    staticPathMemo = buildStaticPath();
  }
  return staticPathMemo;
}

/* -------------------------------------------------------- field <-> box map -- */
/* Mirrors what preserveAspectRatio="xMid… slice" does to the <svg> (which is
   max(100%, MIN_RENDER_W) wide and centred), so the canvas and the SVG show
   exactly the same window of the field. */
function mapBoxToField(width, height, crop) {
  const renderW = Math.max(width, MIN_RENDER_W);
  const scale = Math.max(renderW / FIELD_W, height / FIELD_H);
  const x0 = FIELD_W / 2 - width / 2 / scale;
  let y0 = FIELD_H / 2 - height / 2 / scale;
  if (crop === "start") {
    y0 = 0;
  } else if (crop === "end") {
    y0 = FIELD_H - height / scale;
  }
  return { scale, x0, y0 };
}

function canAnimate() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    typeof window.requestAnimationFrame === "function" &&
    !window.matchMedia(REDUCED_QUERY).matches
  );
}

/* ------------------------------------------------------------------- view -- */
const TONE_CLASS = {
  paper: styles.tonePaper,
  olive: styles.toneOlive,
  void: styles.toneVoid
};

const CROP_ALIGN = {
  start: "xMidYMin slice",
  center: "xMidYMid slice",
  end: "xMidYMax slice"
};

function cx(...values) {
  return values.filter(Boolean).join(" ");
}

const StaticContours = React.memo(function StaticContours({ crop, flip }) {
  return (
    <svg
      className={cx(styles.svg, flip && styles.flip)}
      viewBox={`0 0 ${FIELD_W} ${FIELD_H}`}
      preserveAspectRatio={CROP_ALIGN[crop] || CROP_ALIGN.center}
      focusable="false"
      aria-hidden="true"
    >
      <path className={styles.line} d={getTopoPath()} />
    </svg>
  );
});

function Topo({
  tone = "paper",
  variant = "static",
  crop = "center",
  flip = false,
  className,
  style,
  ...rest
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [motionOk, setMotionOk] = useState(canAnimate);
  const [canvasOk, setCanvasOk] = useState(true);
  const isHero = variant === "hero";
  const animated = isHero && motionOk && canvasOk;

  // Follow the OS setting live: turning reduced motion on swaps the canvas for
  // the static SVG (and the effect below tears the loop down).
  useEffect(() => {
    if (!isHero || typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const query = window.matchMedia(REDUCED_QUERY);
    const sync = () => setMotionOk(canAnimate());
    sync();
    if (query.addEventListener) {
      query.addEventListener("change", sync);
      return () => query.removeEventListener("change", sync);
    }
    if (query.addListener) {
      query.addListener(sync);
      return () => query.removeListener(sync);
    }
    return undefined;
  }, [isHero]);

  useEffect(() => {
    if (!animated) {
      return undefined;
    }
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) {
      return undefined;
    }

    let ctx = null;
    try {
      ctx = canvas.getContext("2d");
    } catch (error) {
      ctx = null;
    }
    if (!ctx) {
      setCanvasOk(false);
      return undefined;
    }

    let width = 0;
    let height = 0;
    let dpr = 1;
    let cols = 0;
    let rows = 0;
    let cellW = HERO_CELL_PX;
    let cellH = HERO_CELL_PX;
    let values = null;
    let map = mapBoxToField(MIN_RENDER_W, 1, crop);
    let sigma = 240;
    let stroke = "";

    let raf = 0;
    let running = false;
    let onScreen = true;
    let last = 0;
    let time = 0;

    // pointer: raw client coords in, eased box coords out
    let seen = false;
    let clientX = 0;
    let clientY = 0;
    let pushX = 0;
    let pushY = 0;
    let strength = 0;
    let strengthTarget = 0;

    const plot = (levelIndex, ax, ay, bx, by) => {
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
    };

    const drawFrame = () => {
      if (!values) {
        return;
      }
      const inv = 1 / map.scale;
      const pushing = strength > 0.002;
      const falloff = 1 / (2 * sigma * sigma);
      let index = 0;
      for (let j = 0; j <= rows; j += 1) {
        const y = j * cellH;
        for (let i = 0; i <= cols; i += 1) {
          const x = i * cellW;
          let sampleX = x;
          let sampleY = y;
          if (pushing) {
            // Radial push: sample the field from a point pulled TOWARD the
            // cursor, so what is drawn here was nearer the cursor — i.e. the
            // lines move outward. weight < 1 keeps the warp fold-free.
            const dx = x - pushX;
            const dy = y - pushY;
            const weight = strength * HERO_PUSH * Math.exp(-(dx * dx + dy * dy) * falloff);
            sampleX = x - dx * weight;
            sampleY = y - dy * weight;
          }
          values[index] = field(map.x0 + sampleX * inv, map.y0 + sampleY * inv, time);
          index += 1;
        }
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.lineWidth = 1;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = stroke;
      ctx.beginPath();
      marchSquares(values, cols, rows, cellW, cellH, plot);
      ctx.stroke();
    };

    const measure = () => {
      const nextWidth = wrap.clientWidth;
      const nextHeight = wrap.clientHeight;
      if (!nextWidth || !nextHeight) {
        return;
      }
      const nextDpr = Math.min(window.devicePixelRatio || 1, HERO_MAX_DPR);
      if (nextWidth !== width || nextHeight !== height || nextDpr !== dpr || !values) {
        width = nextWidth;
        height = nextHeight;
        dpr = nextDpr;
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        cols = Math.max(2, Math.min(HERO_MAX_COLS, Math.ceil(width / HERO_CELL_PX)));
        rows = Math.max(2, Math.min(HERO_MAX_ROWS, Math.ceil(height / HERO_CELL_PX)));
        cellW = width / cols;
        cellH = height / rows;
        values = new Float32Array((cols + 1) * (rows + 1));
        map = mapBoxToField(width, height, crop);
        sigma = Math.max(140, Math.min(320, Math.min(width, height) * 0.3));
      }
      // canvas cannot read var(): take the resolved tone colour off the wrapper
      stroke = window.getComputedStyle(wrap).color;
      drawFrame();
    };

    const tick = (now) => {
      if (!running) {
        return;
      }
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0.016;
      last = now;
      time += dt;

      if (seen) {
        const rect = wrap.getBoundingClientRect();
        const ratio = rect.width ? width / rect.width : 1;
        let localX = (clientX - rect.left) * ratio;
        const localY = (clientY - rect.top) * ratio;
        if (flip) {
          localX = width - localX;
        }
        if (strength < 0.02) {
          // re-entering: start the push where the pointer is, do not sweep in
          pushX = localX;
          pushY = localY;
        } else {
          const ease = 1 - Math.exp(-dt * 7);
          pushX += (localX - pushX) * ease;
          pushY += (localY - pushY) * ease;
        }
      }
      strength += (strengthTarget - strength) * (1 - Math.exp(-dt * 3.2));

      drawFrame();
      raf = window.requestAnimationFrame(tick);
    };

    const start = () => {
      if (running || !onScreen || document.hidden) {
        return;
      }
      running = true;
      last = 0;
      raf = window.requestAnimationFrame(tick);
    };

    const stop = () => {
      running = false;
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    const onMouseMove = (event) => {
      clientX = event.clientX;
      clientY = event.clientY;
      seen = true;
      strengthTarget = 1;
    };
    // touch: keep pushing from the LAST touch point (no release on touchend)
    const onTouch = (event) => {
      const touch = event.touches && event.touches[0];
      if (!touch) {
        return;
      }
      clientX = touch.clientX;
      clientY = touch.clientY;
      seen = true;
      strengthTarget = 1;
    };
    const onPointerGone = () => {
      strengthTarget = 0;
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    };

    const passive = { passive: true };
    window.addEventListener("mousemove", onMouseMove, passive);
    window.addEventListener("touchstart", onTouch, passive);
    window.addEventListener("touchmove", onTouch, passive);
    window.addEventListener("blur", onPointerGone);
    document.documentElement.addEventListener("mouseleave", onPointerGone);
    document.addEventListener("visibilitychange", onVisibility);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(wrap);
    } else {
      window.addEventListener("resize", measure);
    }

    let intersectionObserver = null;
    if (typeof IntersectionObserver !== "undefined") {
      // -1px root margin: a box that merely TOUCHES the viewport edge (scrolled
      // exactly past) reports isIntersecting with a zero ratio and would keep
      // the loop alive for nothing.
      intersectionObserver = new IntersectionObserver(
        (entries) => {
          onScreen = entries[entries.length - 1].isIntersecting;
          if (onScreen) {
            start();
          } else {
            stop();
          }
        },
        { rootMargin: "-1px 0px -1px 0px" }
      );
      intersectionObserver.observe(wrap);
    }

    measure();
    start();

    return () => {
      stop();
      window.removeEventListener("mousemove", onMouseMove, passive);
      window.removeEventListener("touchstart", onTouch, passive);
      window.removeEventListener("touchmove", onTouch, passive);
      window.removeEventListener("blur", onPointerGone);
      document.documentElement.removeEventListener("mouseleave", onPointerGone);
      document.removeEventListener("visibilitychange", onVisibility);
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", measure);
      }
      if (intersectionObserver) {
        intersectionObserver.disconnect();
      }
    };
    // `tone` is listed on purpose: the stroke colour is read off the wrapper
    // once per measure(), so a tone change has to re-run the effect or the
    // canvas keeps drawing the previous surface's line colour until a resize.
  }, [animated, crop, flip, tone]);

  return (
    <div
      {...rest}
      ref={wrapRef}
      className={cx(styles.root, TONE_CLASS[tone] || TONE_CLASS.paper, className)}
      style={{ "--topo-min-w": `${MIN_RENDER_W}px`, ...style }}
      aria-hidden="true"
    >
      {animated ? (
        <canvas ref={canvasRef} className={cx(styles.canvas, flip && styles.flip)} />
      ) : (
        <StaticContours crop={crop} flip={flip} />
      )}
    </div>
  );
}

export default Topo;
